import * as React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { DragDropContext, Droppable, Draggable, DropResult, DraggableProvided, DraggableStateSnapshot, DraggableRubric } from '@hello-pangea/dnd'
import { Style } from '../../styles'
import Icon from '../Icons/Icon'
import { usePopper } from 'react-popper'
import FilterCheckbox from '../Checkbox/FilterCheckbox'
import { ResourceColumn } from '../../types'
import Fuse from 'fuse.js'

const Container = styled.div`
	position: relative;
	display: flex;
	flex: 1 1 0%;
	align-items: center;
	left: auto;
	flex: 0 1 auto;

	&:only-child {
		margin-left: 0;
		flex: 1;
		display: flex;
    justify-content: flex-end;
	}
`

const Wrapper = styled.div`
	position: relative;
  cursor: pointer;
`

const ColumnSettings = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 38px;
	height: 38px;
	font-size: 20px;
	cursor: pointer;
  
  i {
    color: #9aa5b1;
  }

  svg {
    fill: #9aa5b1;
  }
`

const ColumnContainer = styled.div`
  margin: ${Style.spacing.x1};
  transition: all ${Style.animations.baseTransitionSpeed} linear;
  z-index: 5;
`

const ColumnWrapper = styled.form<{ active: boolean }>`
  position: relative;
  z-index: 0;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 0 0 1px rgb(136 152 170 / 10%), 0 15px 35px 0 rgb(49 49 93 / 10%), 0 5px 15px 0 rgb(0 0 0 / 8%);
  min-width: 250px;
  transition: all ${Style.animations.baseTransitionSpeed} linear;
  transform: translateY(-10px);

  ${props => props.active && css`
    transform: translateY(0px);
  `}
`

const Content = styled.div`
  max-height: 250px;
  min-height: 250px;
  overflow-x: hidden;
  overflow-y: auto;
`

const ColumnSection = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #e3e8ee;
  padding: 4px 10px;

  &:last-child {
    border-bottom: none;
  }
`

const ColumnSectionTitle = styled.div`
  position: sticky;
  top: 0;
  background: white;
  z-index: 9999999999;
  padding: ${Style.spacing.x1} ${Style.spacing.x1};
  margin-left: -8px;
  margin-right: -8px;
  color: #596171;
  font-size: 12px;
  font-weight: 700;
  text-align: left;
`

const ColumnPicker = styled.div`
  display: flex;
  flex-direction: column;
`

const ColumnPickerItems = styled.div`
  display: flex;
  flex-direction: column;
`

const ColumnPickerItem = styled.div`
  box-shadow: inset 0 -1px #e3e8ee;
  background: white;

  &:last-child {
    box-shadow: none;
  }
`

const ColumnPickerItemContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${Style.spacing.x1} 0;
  color: #3c4257;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  gap: 8px;
  padding-right: 8px;
`

const ColumnPickerItemContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  text-align: left;
`

const ColumnPickerItemHandle = styled.div`
  width: 10px;
  color: #9aa5b1;
  cursor: grab;
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${Style.spacing.x1};
  background: #f7fafc;
  justify-content: space-between;
  box-shadow: inset 0 -1px #e3e8ee;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
`

const Search = styled.div`
  width: 100%;
  max-width: 100%;
  padding: ${Style.spacing.x1} ${Style.spacing.x1};
`

interface IProps<T> {
  columns: ResourceColumn[]
  activeColumns: string[]
  onChange: (columns: string[]) => void
}

const ResourceColumnSettings = (props: IProps<any>) => {
  const { t } = useTranslation()
  const container = React.useRef<HTMLDivElement>()
  const [active, setActive] = React.useState(false);
  const [referenceElement, setReferenceElement] = React.useState(null);
  const [popperElement, setPopperElement] = React.useState(null);
  const [search, setSearch] = React.useState('')
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'top',
    modifiers: [
      { name: 'arrow' },
      {
        name: 'preventOverflow',
        options: {
          padding: 8,
        },
      },
    ],
  });
  const { columns, activeColumns } = props

  const availableColumns = React.useMemo(() => {
    return columns.filter(column => !activeColumns.includes(column.value))
  }, [columns, activeColumns])

  const fuseOptions = {
    keys: ['label'],
    threshold: 0.3,
  }

  const filteredActiveColumns = React.useMemo(() => {
    if (!search.trim()) return activeColumns

    const activeColumnObjects = activeColumns.map(value => {
      const column = columns.find(col => col.value === value)
      return { value, label: column?.label || value }
    })

    const fuse = new Fuse(activeColumnObjects, fuseOptions)
    const results = fuse.search(search)
    return results.map(result => result.item.value)
  }, [activeColumns, columns, search])

  const filteredAvailableColumns = React.useMemo(() => {
    if (!search.trim()) return availableColumns

    const fuse = new Fuse(availableColumns, fuseOptions)
    const results = fuse.search(search)
    return results.map(result => result.item)
  }, [availableColumns, search])

  React.useEffect(() => {
    if (active) {
      document.addEventListener('mousedown', onOutsideClick);
    } else {
      document.removeEventListener('mousedown', onOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', onOutsideClick);
    }
  }, [active])

  const onToggleFilter = () => {
    setActive(!active)
  }

  const onOutsideClick = (e) => {
    if (active && container && !container.current.contains(e.target)) {
      setActive(false)
      setSearch('')
    }
  }

  const onRemoveColumn = (index: number) => {
    const updateColumns = activeColumns.filter((col, i) => i !== index)

    props.onChange(updateColumns)
  }

  const onAddColumn = (columnValue: string) => {
    const updateColumns = [...activeColumns, columnValue]

    props.onChange(updateColumns)
  }

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result

    if (!destination) return

    if (destination.index === source.index) return

    const updateColumns = [...activeColumns]
    const [removed] = updateColumns.splice(source.index, 1)
    updateColumns.splice(destination.index, 0, removed)

    props.onChange(updateColumns)
  }

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
  }

  const renderDraggableItem = (activeColumn: string, index: number, provided: DraggableProvided) => {
    const activeColumnLabel = columns.find(col => col.value === activeColumn)?.label || activeColumn
    return (
      <ColumnPickerItem
        ref={provided.innerRef}
        {...provided.draggableProps}
      >
        <ColumnPickerItemContent>
          <ColumnPickerItemContentWrapper onClick={() => onRemoveColumn(index)}>
            <FilterCheckbox checked={true} onChange={() => { }} />
            {activeColumnLabel}
          </ColumnPickerItemContentWrapper>

          <ColumnPickerItemHandle {...provided.dragHandleProps}>
            <Icon icon='grip-vertical' />
          </ColumnPickerItemHandle>
        </ColumnPickerItemContent>
      </ColumnPickerItem>
    )
  }

  const renderClone = (provided: DraggableProvided, _snapshot: DraggableStateSnapshot, rubric: DraggableRubric) => {
    const activeColumn = activeColumns[rubric.source.index]
    return renderDraggableItem(activeColumn, rubric.source.index, provided)
  }

  return (
    <Container ref={container}>
      <Wrapper ref={setReferenceElement} onClick={onToggleFilter}>
        <ColumnSettings>
          <Icon icon='setting' />
        </ColumnSettings>
      </Wrapper>

      <ColumnContainer
        ref={setPopperElement}
        style={{
          ...styles.popper,
          visibility: active ? 'visible' : 'hidden',
          pointerEvents: active ? 'all' : 'none',
          opacity: active ? 1 : 0,
          zIndex: 16,
        }}
        {...attributes.popper}
      >
        <ColumnWrapper active={active}>
          <Header>
            <div />
            {t('ResourceColumnSettings::Columns')}
            <div />
          </Header>
          <Content>
            <Search>
              <input
                type='text'
                value={search}
                placeholder={t('ResourceColumnSettings::Search columns...')}
                onChange={onSearchChange}
              />
            </Search>

            <ColumnSection>
              <ColumnSectionTitle>{t('ResourceColumnSettings::Active columns')}</ColumnSectionTitle>
              <ColumnPicker>
                {filteredActiveColumns?.length === 0 && (
                  <div style={{ padding: Style.spacing.x2, color: '#9aa5b1', fontSize: 14 }}>
                    {search.trim() ? t('ResourceColumnSettings::No matching columns') : t('ResourceColumnSettings::No active columns')}
                  </div>
                )}
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId='active-columns' renderClone={renderClone}>
                    {(droppableProvided) => (
                      <ColumnPickerItems
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                      >
                        {filteredActiveColumns?.map((activeColumn) => {
                          const originalIndex = activeColumns.indexOf(activeColumn)
                          return (
                            <Draggable key={activeColumn} draggableId={activeColumn} index={originalIndex}>
                              {(draggableProvided) => renderDraggableItem(activeColumn, originalIndex, draggableProvided)}
                            </Draggable>
                          )
                        })}
                        {droppableProvided.placeholder}
                      </ColumnPickerItems>
                    )}
                  </Droppable>
                </DragDropContext>
              </ColumnPicker>
            </ColumnSection>

            <ColumnSection>
              <ColumnSectionTitle>{t('ResourceColumnSettings::Available columns')}</ColumnSectionTitle>
              <ColumnPicker>
                {filteredAvailableColumns?.length === 0 && (
                  <div style={{ padding: Style.spacing.x2, color: '#9aa5b1', fontSize: 14 }}>
                    {search.trim() ? t('ResourceColumnSettings::No matching columns') : t('ResourceColumnSettings::No available columns')}
                  </div>
                )}
                {filteredAvailableColumns?.map((column) => {
                  return (
                    <ColumnPickerItem key={column.value}>
                      <ColumnPickerItemContent onClick={() => onAddColumn(column.value)}>
                        <ColumnPickerItemContentWrapper>
                          <FilterCheckbox checked={false} onChange={() => { }} />
                          {column.label}
                        </ColumnPickerItemContentWrapper>
                      </ColumnPickerItemContent>
                    </ColumnPickerItem>
                  )
                })}
              </ColumnPicker>
            </ColumnSection>
          </Content>
          <input type='submit' style={{ display: 'none' }} />
        </ColumnWrapper>
      </ColumnContainer>
    </Container >
  )
}

export default ResourceColumnSettings